import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { CalendarX, CalendarDays, Clock, ShoppingCart, Package } from 'lucide-react';

export default function MiHistorial() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('ventas');

  const fetchData = useCallback(async () => {
    try { const res = await api.getMiHistorial(); setData(res); }
    catch (error) { /* handled */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="text-gray-500 text-[13px]">Cargando...</div></div>);
  }

  const ventasPorProducto = data?.ventas_por_producto || {};
  const productEntries = Object.entries(ventasPorProducto).sort((a, b) => b[1] - a[1]);

  const tabs = [
    { id: 'ventas', label: 'Mis Ventas', icon: ShoppingCart },
    { id: 'faltas', label: 'Mis Faltas', icon: CalendarX },
    { id: 'licencias', label: 'Mis Licencias', icon: CalendarDays },
  ];

  return (
    <div className="space-y-6" data-testid="mi-historial">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Mi Historial</h1>
        <p className="text-[13px] text-gray-500 mt-0.5">Tu actividad y registros</p>
      </div>

      {/* Faltas Este Mes */}
      <div className="metric-card inline-flex flex-col" data-testid="metric-faltas-mes">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-md bg-[#F59E0B]/10 flex items-center justify-center"><CalendarX size={16} className="text-[#F59E0B]" /></div>
        </div>
        <div className="metric-value">{data?.faltas_mes?.length || 0}</div>
        <div className="metric-label">Faltas Este Mes</div>
      </div>

      {/* Ventas Discriminadas por Producto */}
      <div className="mac-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Package size={16} className="text-[#0082D2]" />
          <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Ventas Este Mes por Producto</h3>
        </div>
        {productEntries.length === 0 ? (
          <p className="text-[13px] text-gray-400 text-center py-4">Sin ventas este mes</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {productEntries.map(([producto, cantidad]) => (
              <div key={producto} className="bg-[#F3F4F6] rounded-md p-4 text-center">
                <div className="text-2xl font-bold text-[#0082D2]">{cantidad}</div>
                <div className="text-[12px] text-gray-600 mt-1 font-medium">{producto}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {data?.horario && (data.horario.hora_entrada || data.horario.hora_salida) && (
        <div className="mac-card p-5">
          <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Mi Horario</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-blue-50 rounded-md p-3 text-center"><Clock size={16} className="mx-auto text-blue-500 mb-1" /><div className="text-[11px] text-gray-500">Entrada</div><div className="text-[14px] font-semibold text-[#0A0A0A]">{data.horario.hora_entrada || '-'}</div></div>
            <div className="bg-orange-50 rounded-md p-3 text-center"><Clock size={16} className="mx-auto text-orange-500 mb-1" /><div className="text-[11px] text-gray-500">Sale descanso</div><div className="text-[14px] font-semibold text-[#0A0A0A]">{data.horario.hora_descanso_salida || '-'}</div></div>
            <div className="bg-green-50 rounded-md p-3 text-center"><Clock size={16} className="mx-auto text-green-500 mb-1" /><div className="text-[11px] text-gray-500">Vuelve descanso</div><div className="text-[14px] font-semibold text-[#0A0A0A]">{data.horario.hora_descanso_vuelta || '-'}</div></div>
            <div className="bg-red-50 rounded-md p-3 text-center"><Clock size={16} className="mx-auto text-red-500 mb-1" /><div className="text-[11px] text-gray-500">Salida</div><div className="text-[14px] font-semibold text-[#0A0A0A]">{data.horario.hora_salida || '-'}</div></div>
          </div>
        </div>
      )}

      <div className="flex gap-1 bg-gray-100 rounded-md p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm text-[13px] font-medium transition-all ${tab === t.id ? 'bg-white text-[#00205B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'ventas' && (
        <div className="mac-card overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200"><h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Historial de Ventas</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="table-header">Fecha</th><th className="table-header">Socio</th><th className="table-header">Productos</th><th className="table-header">Mutualista Anterior</th></tr></thead>
              <tbody>
                {!data?.ventas?.length ? (<tr><td colSpan="4" className="table-cell text-center text-gray-500">Sin ventas registradas</td></tr>
                ) : (data.ventas.map((venta, i) => (
                  <tr key={venta.id || `venta-${venta.fecha}-${i}`} className="table-row">
                    <td className="table-cell text-[12px] text-gray-500">{new Date(venta.fecha).toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td className="table-cell font-medium text-[#0A0A0A]">{venta.nombre_socio} {venta.apellido_socio}</td>
                    <td className="table-cell"><div className="flex flex-wrap gap-1">{venta.servicios.map((s, j) => (<span key={`${venta.id}-svc-${j}`} className="inline-flex px-2 py-0.5 rounded-full bg-[#0082D2]/10 text-[#0082D2] text-[10px] font-medium">{s}</span>))}</div></td>
                    <td className="table-cell text-[12px] text-gray-500">{venta.mutualista_anterior}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'faltas' && (
        <div className="mac-card p-5">
          <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Faltas del Mes Actual</h3>
          {data?.faltas_mes?.length > 0 ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {data.faltas_mes.map(fecha => (
                  <span key={fecha} className="inline-flex items-center px-3 py-1.5 rounded-md bg-orange-50 text-[13px] font-medium text-[#F59E0B] border border-orange-200">
                    <CalendarX size={14} className="mr-2" />
                    {new Date(fecha + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                ))}
              </div>
              <p className="text-[12px] text-gray-500">Total: {data.faltas_mes.length} dia(s) de falta este mes</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarDays size={40} className="mx-auto text-green-200 mb-3" />
              <p className="text-[13px] text-green-600 font-medium">Asistencia completa este mes</p>
            </div>
          )}
        </div>
      )}

      {tab === 'licencias' && (
        <div className="mac-card overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200"><h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Mis Licencias</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="table-header">Tipo</th><th className="table-header">Desde</th><th className="table-header">Hasta</th><th className="table-header">Notas</th></tr></thead>
              <tbody>
                {!data?.licencias?.length ? (<tr><td colSpan="4" className="table-cell text-center text-gray-500">Sin licencias registradas</td></tr>
                ) : (data.licencias.map((lic, i) => (
                  <tr key={lic.id || `lic-${lic.fecha_inicio}-${i}`} className="table-row">
                    <td className="table-cell">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${lic.tipo === 'anual' ? 'bg-[#0082D2]/10 text-[#0082D2]' : lic.tipo === 'enfermedad' ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-gray-100 text-gray-600'}`}>{lic.tipo}</span>
                    </td>
                    <td className="table-cell text-[12px]">{new Date(lic.fecha_inicio + 'T12:00:00').toLocaleDateString('es-UY')}</td>
                    <td className="table-cell text-[12px]">{new Date(lic.fecha_fin + 'T12:00:00').toLocaleDateString('es-UY')}</td>
                    <td className="table-cell text-[12px] text-gray-500">{lic.notas || '-'}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

